import React, { useEffect, useRef } from 'react';

// Hiệu ứng "Pet Pet" (petpetgif): bàn tay vỗ đầu, avatar bị nhún dẹp theo từng khung — vẽ thuần canvas,
// KHÔNG thư viện/asset ngoài (hợp CSP). Bảng biến dạng cổ điển: dx, dy, dw, dh (tỉ lệ theo hộp avatar).
const FRAMES: [number, number, number, number][] = [
  [0.0, 0.0, 1.0, 1.0],
  [-0.03, 0.1, 1.06, 0.9],
  [-0.06, 0.22, 1.12, 0.78],
  [-0.03, 0.1, 1.06, 0.9],
  [0.0, 0.0, 1.0, 1.0],
];
const FRAME_MS = 80; // đổi khung mỗi 80ms (tốc độ vỗ)
const DURATION_MS = 2600; // chạy rồi tự đóng

type Props = { src?: string; label?: string; onDone?: () => void };

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  if ((ctx as any).roundRect) {
    ctx.beginPath();
    (ctx as any).roundRect(x, y, w, h, r);
  } else {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
};

const PetPet: React.FC<Props> = ({ src, label, onDone }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const doneRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const S = canvas.width;
    const boxW = S * 0.6;
    const boxH = S * 0.6;
    const boxX = (S - boxW) / 2;
    const boxBottom = S * 0.94; // đáy avatar cố định (nhún từ trên xuống)

    const img = new Image(); // KHÔNG set crossOrigin: chỉ hiển thị (không export) nên canvas taint cũng OK, tránh lỗi CORS
    let raf = 0;
    let start = 0;

    // Bàn tay vỗ từ trên: lòng bàn tay + 5 ngón chụm xuống, hạ thấp theo mức nhún.
    const drawHand = (press: number) => {
      const hw = boxW * 1.15;
      const hx = boxX + boxW / 2 - hw / 2;
      const palmH = S * 0.16;
      const top = boxBottom - boxH - palmH * 0.35 + press;
      ctx.save();
      ctx.fillStyle = '#f6c8a3';
      ctx.strokeStyle = '#dd9f76';
      ctx.lineWidth = 2;
      // các ngón (vẽ trước để lòng bàn tay đè lên gốc ngón)
      const fingers = 5;
      const gap = hw / fingers;
      for (let i = 0; i < fingers; i++) {
        const fw = gap * 0.62;
        const fx = hx + gap * i + (gap - fw) / 2;
        const fh = palmH * (0.85 + (i === 0 || i === fingers - 1 ? -0.15 : 0.1));
        roundRect(ctx, fx, top + palmH * 0.5, fw, fh, fw / 2);
        ctx.fill();
        ctx.stroke();
      }
      // lòng bàn tay
      roundRect(ctx, hx, top, hw, palmH, palmH * 0.4);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    };

    const render = (t: number) => {
      if (!start) start = t;
      const elapsed = t - start;
      const [dx, , dw, dh] = FRAMES[Math.floor(elapsed / FRAME_MS) % FRAMES.length];

      ctx.clearRect(0, 0, S, S);

      const w = boxW * dw;
      const h = boxH * dh;
      const cx = boxX + boxW / 2 + boxW * dx; // dx: dịch ngang khi phình ra
      const y = boxBottom - h; // neo đáy để nhún dẹp xuống

      // avatar bo tròn (nhún thành ellipse khi bị vỗ)
      ctx.save();
      ctx.beginPath();
      ctx.ellipse(cx, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      if (img.complete && img.naturalWidth) {
        ctx.drawImage(img, cx - w / 2, y, w, h);
      } else {
        ctx.fillStyle = '#c7d2fe';
        ctx.fillRect(cx - w / 2, y, w, h);
      }
      ctx.restore();
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,.15)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(cx, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      drawHand((1 - dh) * boxH); // nhún càng sâu → tay hạ càng thấp

      if (elapsed >= DURATION_MS) {
        if (!doneRef.current) {
          doneRef.current = true;
          onDone?.();
        }
        return;
      }
      raf = requestAnimationFrame(render);
    };

    img.onload = () => (raf = requestAnimationFrame(render));
    img.onerror = () => (raf = requestAnimationFrame(render));
    img.src = src || '/default-avatar.svg';

    return () => cancelAnimationFrame(raf);
  }, [src, onDone]);

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={() => onDone?.()}
    >
      <canvas ref={canvasRef} width={240} height={240} className="drop-shadow-2xl" />
      {label && (
        <div className="mt-1 text-white font-semibold text-lg drop-shadow text-center px-4">{label}</div>
      )}
    </div>
  );
};

export default PetPet;
